import { v4 as uuid } from 'uuid';

import {
  AdderCustomColumnHeader,
  CustomColumnHeader,
  CustomColumnType,
  getAdderColumnType,
  getColumnHeaderDataTypes,
  getNumericDataType,
  ID,
  Lot,
  LotChanges
} from 'types';
import { Global } from '@utils/common';
import { matchesNew } from './matchesNew';

export const sortByOrder = (a: CustomColumnHeader, b: CustomColumnHeader): number => (
  a.column_order_index - b.column_order_index
);

const isNewCustomColumnHeader = (customColumnHeader: CustomColumnHeader): boolean => (
  // Not filtering out deleted columns here, because we need it for the UI.
  matchesNew(customColumnHeader.id)
);

const getNewCustomColumnHeaders = (customColumnHeaderChanges?: Record<ID, CustomColumnHeader>): CustomColumnHeader[] => {
  const hasLotChanges = customColumnHeaderChanges && Object.keys(customColumnHeaderChanges)?.length;

  if (!hasLotChanges) return [];
  return Object.values(customColumnHeaderChanges).filter(isNewCustomColumnHeader);
};

interface CreateDefaultHeaderParams {
  isSupplierVisible: boolean;
  lotId: ID;
  order?: number;
  title: string;
}

export const DEFAULT_ADDER_COLUMN_CONFIG = { is_required: true, lot_ids: null, scope: 'per_line', taxonomy: null } as const;

export const createDefaultAdderHeader = ({ lotId, order = 0 }: Pick<CreateDefaultHeaderParams, 'lotId' | 'order'>): AdderCustomColumnHeader => ({
  _adding: true,
  _destroy: false,
  adder_column_config: { ...DEFAULT_ADDER_COLUMN_CONFIG },
  character_limit: 50, // Required by CustomColumnHeader interface; unused for numeric adder columns
  column_order_index: order,
  column_type: getAdderColumnType(),
  created_at: '',
  data_type: getNumericDataType(),
  display_text: '',
  id: `new-${uuid()}`,
  is_supplier_visible: true,
  lot_id: lotId,
  qualifying_equality: '',
});

export const createDefaultHeader = ({ lotId, order = 0, title, isSupplierVisible }: CreateDefaultHeaderParams): CustomColumnHeader => ({
  _adding: true,
  _destroy: false,
  character_limit: 50,
  column_order_index: order,
  column_type: Global.lots.custom_column_headers.column_types.descriptive as CustomColumnType,
  created_at: '',
  data_type: getColumnHeaderDataTypes().TEXT,
  display_text: title,
  id: `new-${uuid()}`,
  is_supplier_visible: isSupplierVisible,
  lot_id: lotId,
  qualifying_equality: ''
});

export const mergeCustomColumnHeaders = (lot: Lot, lotChanges?: Record<ID, LotChanges>): CustomColumnHeader[] => {
  const hasLotChanges = lotChanges && Object.keys(lotChanges)?.length;
  if (!hasLotChanges) return lot.custom_column_headers;

  const customColumnHeaders: CustomColumnHeader[] = lot.custom_column_headers;
  const customColumnHeadersChanges: Record<ID, CustomColumnHeader> = lotChanges?.[lot.id]?.custom_column_headers || {};
  const hasCustomColumnHeadersChanges = customColumnHeadersChanges && Object.keys(customColumnHeadersChanges).length;
  const result: Record<ID, CustomColumnHeader> = {};

  if (hasCustomColumnHeadersChanges) {
    if (customColumnHeaders?.length) {
      const changedCustomColumnHeaders = customColumnHeaders.map((customColumnHeader: CustomColumnHeader) => ({
        ...customColumnHeader,
        ...customColumnHeadersChanges[customColumnHeader.id],
      }));

      // Update existing customColumnHeaders with changes.
      changedCustomColumnHeaders.forEach((customColumnHeader: CustomColumnHeader) => {
        result[customColumnHeader.id] = { ...result[customColumnHeader.id], ...customColumnHeader };
      });
    }
    const newCustomColumnHeaders = getNewCustomColumnHeaders(customColumnHeadersChanges);

    // Add new customColumnHeaders.
    newCustomColumnHeaders.forEach((customColumnHeader: CustomColumnHeader) => {
      result[customColumnHeader.id] = { ...result[customColumnHeader.id], ...customColumnHeader };
    });
  } else if (customColumnHeaders?.length) {
    customColumnHeaders.forEach((customColumnHeader: CustomColumnHeader) => {
      result[customColumnHeader.id] = { ...result[customColumnHeader.id], ...customColumnHeader };
    });
  }

  return Object.values(result).map((customColumnHeader: CustomColumnHeader) => ({
    ...customColumnHeader,
    lot_id: lot.id,
  })).sort(sortByOrder);
};
